import { VOIP_PUSH_PLATFORM } from 'app/services/deviceTelemetry/deviceTelemetry.constants';
import telnyxService from 'app/services/telnyx/telnyxService';
/**
 * webTelephonyTransport — the browser implementation of the telephony transport.
 *
 * Behavior-preserving wrapper around the existing web Telnyx integration
 * (app/services/telnyx/telnyxService + TelnyxProvider + @telnyx/webrtc). The
 * call-control verbs delegate verbatim to telnyxService, so the outbound and
 * inbound web call flow is unchanged. Lifecycle (login / token refresh) and event
 * emission remain owned by TelnyxProvider; this transport exposes the same
 * emitter so the provider can publish transport-level events for any consumer
 * that is written against the abstract interface.
 *
 * login / registerPushToken / deregisterPushToken are intentionally no-ops on
 * web: the Telnyx JWT lifecycle is handled inside TelnyxProvider, and native push
 * tokens do not exist in the browser.
 */
import _ from 'lodash';

import { logger } from 'utils/logger';

import { createTransportEmitter } from './telephonyTransport.utils';

const emitter = createTransportEmitter();

// Web token/login lifecycle is owned by TelnyxProvider — nothing to do here.
const login = () => Promise.resolve();

// Lazy-idle disconnect is a native-only concern (DEV-4332): on web the standing Telnyx connection is owned
// by TelnyxProvider and is intentionally NOT torn down per call — no-op so callers invoke the interface
// uniformly across platforms.
const disconnect = () => {
  logger.log('[TelephonyTransport] disconnect is a no-op on web');

  return Promise.resolve();
};

const dial = ({ destinationNumber, callerNumber, callerName, clientState }) =>
  telnyxService.makeCall({ destinationNumber, callerNumber, callerName, clientState });

const answer = () => {
  telnyxService.answer();
};

const hangup = () => {
  telnyxService.hangup();
};

// The current web telephony UI does not expose a mute control; the web Telnyx
// SDK exposes muteAudio/unmuteAudio on the active call. We delegate when present
// so the abstract interface is complete without altering any existing flow.
const mute = isMuted => {
  const { currentCall } = telnyxService;

  if (!currentCall) {
    return;
  }

  const toggle = isMuted ? currentCall.muteAudio : currentCall.unmuteAudio;

  if (_.isFunction(toggle)) {
    toggle.call(currentCall);
  }
};

const hold = isHeld => {
  if (isHeld) {
    telnyxService.hold();

    return;
  }

  telnyxService.unhold();
};

const sendDtmf = digit => {
  telnyxService.sendDtmf(digit);
};

// Native push registration has no browser equivalent — no-op so callers can
// invoke the interface uniformly regardless of platform.
const registerPushToken = () => {
  logger.log('[TelephonyTransport] registerPushToken is a no-op on web');

  return Promise.resolve();
};

const deregisterPushToken = () => {
  logger.log('[TelephonyTransport] deregisterPushToken is a no-op on web');

  return Promise.resolve();
};

// Cold-wake helpers have no browser equivalent (FCM push launches only the native shell) — no-ops
// so the interface is uniform across platforms.
const getPendingPushCall = () => {
  logger.log('[TelephonyTransport] getPendingPushCall is a no-op on web');

  return Promise.resolve(null);
};

const connectFromPush = () => {
  logger.log('[TelephonyTransport] connectFromPush is a no-op on web');

  return Promise.resolve();
};

// VoIP (PushKit) tokens are iOS-only — no browser equivalent.
const getVoipToken = () => {
  logger.log('[TelephonyTransport] getVoipToken is a no-op on web');

  return Promise.resolve({ token: null, platform: VOIP_PUSH_PLATFORM });
};

const webTelephonyTransport = {
  login,
  disconnect,
  dial,
  answer,
  hangup,
  mute,
  hold,
  sendDtmf,
  registerPushToken,
  deregisterPushToken,
  getPendingPushCall,
  connectFromPush,
  getVoipToken,
  on: emitter.on,
  off: emitter.off,
  emit: emitter.emit,
};

export default webTelephonyTransport;
